import { API_PREFIX, type ApiErrorBody } from '@edms/contracts';

/**
 * Client HTTP de l'application.
 *
 * Trois responsabilités, et pas une de plus :
 *  - porter le jeton d'accès, conservé **en mémoire** et jamais dans le
 *    `localStorage` — un jeton persisté est un jeton exfiltrable par XSS ;
 *  - renouveler la session de façon transparente quand le jeton expire, en
 *    dédoublonnant les renouvellements concurrents ;
 *  - transformer les erreurs de l'API en objets typés exploitables par l'UI.
 */

export class ApiError extends Error {
  readonly code: string;
  readonly statusCode: number;
  readonly correlationId: string;
  readonly details: readonly { field: string; message: string }[];

  constructor(body: ApiErrorBody) {
    super(body.message);
    this.name = 'ApiError';
    this.code = body.code;
    this.statusCode = body.statusCode;
    this.correlationId = body.correlationId;
    this.details = body.details ?? [];
  }

  /** Messages d'erreur indexés par champ, pour les formulaires. */
  get fieldErrors(): Record<string, string> {
    return Object.fromEntries(this.details.map((detail) => [detail.field, detail.message]));
  }
}

type AccessTokenListener = (token: string | null) => void;

let accessToken: string | null = null;
let refreshPromise: Promise<boolean> | null = null;
const listeners = new Set<AccessTokenListener>();

export function setAccessToken(token: string | null): void {
  accessToken = token;
  for (const listener of listeners) listener(token);
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function onAccessTokenChange(listener: AccessTokenListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  signal?: AbortSignal;
  /** Court-circuite le renouvellement automatique (routes d'authentification). */
  skipRefresh?: boolean;
  formData?: FormData;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = `${API_PREFIX}${path.startsWith('/') ? path : `/${path}`}`;
  if (!query) return url;

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    params.set(key, String(value));
  }

  const queryString = params.toString();
  return queryString ? `${url}?${queryString}` : url;
}

async function toApiError(response: Response): Promise<ApiError> {
  try {
    const body = (await response.json()) as ApiErrorBody;
    if (body?.code) return new ApiError(body);
  } catch {
    // Réponse non JSON (proxy, passerelle) : on fabrique une erreur cohérente
    // plutôt que de laisser remonter un objet inexploitable.
  }

  return new ApiError({
    code: 'INTERNAL_ERROR',
    message:
      response.status === 0 || response.status >= 502
        ? 'Le serveur est injoignable. Vérifiez votre connexion réseau.'
        : `Erreur inattendue (${response.status}).`,
    statusCode: response.status,
    timestamp: new Date().toISOString(),
    path: response.url,
    correlationId: response.headers.get('x-correlation-id') ?? 'inconnu',
  });
}

/**
 * Renouvelle le jeton d'accès.
 *
 * Les appels concurrents partagent la même promesse : sans cela, cinq requêtes
 * expirant en même temps déclencheraient cinq rotations, et la rotation des
 * jetons de rafraîchissement invaliderait les quatre dernières.
 */
async function refreshSession(): Promise<boolean> {
  refreshPromise ??= (async () => {
    try {
      const response = await fetch(buildUrl('/auth/refresh'), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        setAccessToken(null);
        return false;
      }

      const body = (await response.json()) as { accessToken: string };
      setAccessToken(body.accessToken);
      return true;
    } catch {
      setAccessToken(null);
      return false;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const send = async (): Promise<Response> => {
    const headers: Record<string, string> = {};
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;

    let body: BodyInit | undefined;
    if (options.formData) {
      // Ne pas fixer `Content-Type` : le navigateur doit générer la frontière
      // multipart lui-même.
      body = options.formData;
    } else if (options.body !== undefined) {
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify(options.body);
    }

    return fetch(buildUrl(path, options.query), {
      method: options.method ?? 'GET',
      headers,
      body,
      credentials: 'include',
      signal: options.signal ?? null,
    });
  };

  let response = await send();

  if (response.status === 401 && !options.skipRefresh) {
    const renewed = await refreshSession();
    if (renewed) {
      response = await send();
    }
  }

  if (!response.ok) {
    throw await toApiError(response);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    return (await response.blob()) as T;
  }

  return (await response.json()) as T;
}

export const api = {
  get: <T>(path: string, query?: RequestOptions['query'], signal?: AbortSignal) =>
    request<T>(path, { method: 'GET', ...(query ? { query } : {}), ...(signal ? { signal } : {}) }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  upload: <T>(path: string, formData: FormData) => request<T>(path, { method: 'POST', formData }),
  refreshSession,
};

/**
 * URL d'une ressource binaire servie par l'API (aperçu de page, PDF).
 *
 * Ces URL sont posées dans des `src`/`href`, qui ne peuvent pas porter d'en-tête
 * `Authorization`. On les récupère donc par `fetch` puis on crée une URL
 * d'objet — ce qui préserve l'authentification et évite d'exposer une URL
 * signée publique.
 */
export async function fetchBlobUrl(path: string): Promise<string> {
  const blob = await request<Blob>(path);
  return URL.createObjectURL(blob);
}
